import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import { getCourses } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SessionManager from '../utils/sessionManager';

const AllCoursesPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, role } = useAuth();
  
  // State management
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 9;

  // Load courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await getCourses();
        setCourses(data);
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  }, []);

  // Load enrolled courses
  useEffect(() => {
    const loadEnrolledCourses = async () => {
      if (isLoggedIn() && role === 'candidate') {
        try {
          const candidateSession = JSON.parse(localStorage.getItem('candidate_session') || '{}');
          const token = candidateSession?.access_token;
          
          if (token) {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/course-teaching/my-courses`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const data = await response.json();
              const enrolledIds = (data.courses || []).map(c => c.id);
              setEnrolledCourseIds(enrolledIds);
            }
          }
        } catch (error) {
          console.error('Error loading enrolled courses:', error);
        }
      }
    };
    
    loadEnrolledCourses();
  }, [isLoggedIn, role]);

  // Get unique departments
  const departments = useMemo(() => {
    const depts = ['All', ...new Set(courses.map(c => c.category))];
    return depts;
  }, [courses]);

  // Filter and sort courses
  const filteredAndSortedCourses = useMemo(() => {
    let filtered = [...courses];

    // Filter by department
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter(c => c.category === selectedDepartment);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query) ||
        c.provider?.toLowerCase().includes(query)
      );
    }

    // Sort courses
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'popular':
        filtered.sort((a, b) => (b.students_enrolled || 0) - (a.students_enrolled || 0));
        break;
      default:
        break;
    }

    // Add calculated original price if not present (for demo purposes)
    return filtered.map(course => {
      if (!course.original_price && course.price) {
        // Calculate a demo original price (20-40% higher)
        const discountPercent = 33; // 33% discount
        const originalPrice = Math.round(course.price / (1 - discountPercent / 100));
        return { ...course, original_price: originalPrice };
      }
      return course;
    });
  }, [courses, selectedDepartment, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCourses.length / coursesPerPage);
  const paginatedCourses = useMemo(() => {
    const startIndex = (currentPage - 1) * coursesPerPage;
    return filteredAndSortedCourses.slice(startIndex, startIndex + coursesPerPage);
  }, [filteredAndSortedCourses, currentPage]);

  // Reset to page 1 when filters change (but don't scroll)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDepartment, searchQuery, sortBy]);

  // Scroll to top only when page changes (not on filter changes)
  useEffect(() => {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // Get course image
  const getCourseImage = (category, index) => {
    const images = {
      'IT & Technology': [
        'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=400&h=250&fit=crop'
      ],
      'Business': [
        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop'
      ],
      'Computer Science': [
        'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1550439062-609e1531270e?w=400&h=250&fit=crop'
      ],
      'Data Science': [
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=250&fit=crop'
      ],
      'Health': [
        'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=400&h=250&fit=crop'
      ],
      'Information Technology': [
        'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop'
      ],
      'Arts and Humanities': [
        'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=250&fit=crop',
        'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=250&fit=crop'
      ]
    };
    
    const categoryImages = images[category] || images['IT & Technology'];
    return categoryImages[index % categoryImages.length];
  };

  const requireAuth = () => {
    // Double-check with SessionManager directly
    const candidateSession = SessionManager.getSession('candidate');
    const hasValidSession = candidateSession && candidateSession.access_token;
    
    console.log('🔐 AllCoursesPage requireAuth check:', { 
      isLoggedInHook: isLoggedIn(), 
      roleFromHook: role,
      hasValidSession,
      candidateSessionExists: !!candidateSession
    });
    
    // Check if logged in and is a candidate
    if (!hasValidSession) {
      console.log('❌ No valid candidate session, redirecting to login');
      navigate('/login');
      return false;
    }
    
    console.log('✅ Auth check passed');
    return true;
  };

  const handleEnroll = (course) => {
    if (requireAuth()) {
      if (enrolledCourseIds.includes(course.id)) {
        navigate('/my-courses');
        return;
      }
      
      // Navigate to billing page
      navigate(`/course-billing/${course.id}`);
    }
  };

  const isEnrolled = (courseId) => {
    return enrolledCourseIds.includes(courseId);
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50">
      <LandingHeader />

      {/* Hero Section */}
      <section className="bg-[#0a1929] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Aptirix Courses
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-6">
              India's First AI-Powered Course Platform
            </p>
            <p className="text-base text-gray-400 leading-relaxed">
              Transform your career with cutting-edge courses powered by artificial intelligence. 
              Get personalized learning paths, real-time feedback, and industry-recognized certifications 
              designed specifically for the Indian job market.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left Sidebar - Filters */}
          <aside className="lg:w-64 flex-shrink-0 w-full lg:self-start">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Filters</h3>
              
              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Courses
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Department Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Department
                </label>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {departments.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => setSelectedDepartment(dept)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                        selectedDepartment === dept
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1">
            {/* Top Bar - Results Count and Sort */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-gray-700">
                  <span className="font-semibold">{filteredAndSortedCourses.length}</span> courses found
                </div>
                
                {/* Sort Dropdown */}
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Courses Grid */}
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading courses...</p>
              </div>
            ) : filteredAndSortedCourses.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-lg shadow-sm border border-gray-200">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No courses found</h3>
                <p className="mt-2 text-gray-500">Try adjusting your filters or search query</p>
              </div>
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {paginatedCourses.map((course, index) => (
                    <div
                      key={course.id}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col"
                    >
                      {/* Course Image */}
                      <div 
                        className="relative h-48 overflow-hidden bg-gray-100 cursor-pointer"
                        onClick={() => navigate(`/courses/${course.id}`)}
                      >
                        <img 
                          src={course.thumbnail || getCourseImage(course.category, index)} 
                          alt={course.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            {course.category}
                          </span>
                        </div>
                        {course.rating >= 4.5 && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-block bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-semibold">
                              Top Rated
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Course Content */}
                      <div className="p-5 flex-1 flex flex-col">
                        <div 
                          className="cursor-pointer flex-1"
                          onClick={() => navigate(`/courses/${course.id}`)}
                        >
                          <p className="text-xs text-gray-500 mb-2">{course.provider}</p>
                          <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 min-h-[3.5rem]">
                            {course.title}
                          </h3>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-yellow-500 fill-current" viewBox="0 0 20 20">
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                              </svg>
                              <span className="font-medium">{course.rating || 4.5}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>{course.duration_weeks} weeks</span>
                            </div>
                            <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium capitalize">
                              {course.level}
                            </span>
                          </div>
                          
                          {course.students_enrolled > 0 && (
                            <p className="text-xs text-gray-500 mb-4">
                              {course.students_enrolled.toLocaleString('en-IN')} students enrolled
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <p className="text-2xl font-bold text-gray-900">
                                ₹{course.price.toLocaleString('en-IN')}
                              </p>
                              {course.original_price && course.original_price > course.price && (
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{course.original_price.toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                            {course.original_price && course.original_price > course.price && (
                              <span className="text-xs text-green-600 font-semibold">
                                {Math.round(((course.original_price - course.price) / course.original_price) * 100)}% OFF
                              </span>
                            )}
                          </div>
                          <Button 
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleEnroll(course); 
                            }}
                          >
                            {isLoggedIn() && role === 'candidate' && isEnrolled(course.id) ? 'Continue' : 'Enroll Now'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <div className="flex gap-2">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return <span key={pageNum} className="px-2 py-2 text-gray-500">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AllCoursesPage;
